import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';
import { playerAPI } from '../../../services/api';
import { apiErrorMessage, unwrapApiBody } from '../../../utils/apiResponse';
import { useHelpWizardOptional } from '../HelpWizardProvider';

const Box = styled.div`
  margin-top: 0.5rem;
  padding: 1rem 1.1rem;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #fff;
`;

const Caution = styled.div`
  margin: 0 0 1rem;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const Summary = styled.p`
  margin: 0 0 1rem;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #334155;
`;

const Field = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: #043663;
`;

const PasswordInput = styled.input`
  display: block;
  width: 100%;
  max-width: 280px;
  margin-top: 0.4rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
`;

const LeaveBtn = styled.button`
  margin-top: 1rem;
  padding: 0.7rem 1.25rem;
  border: none;
  border-radius: 8px;
  background: #b91c1c;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Muted = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: #64748b;
  line-height: 1.45;
`;

/**
 * @param {{ teamContext?: { teamId?: string, teamName?: string } }} props
 */
export default function HelpSelfLeaveTeamPanel({ teamContext }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const wizard = useHelpWizardOptional();
  const teamId = teamContext?.teamId;
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [password, setPassword] = useState('');
  const [preview, setPreview] = useState(null);
  const [done, setDone] = useState(false);

  const load = useCallback(async () => {
    if (!teamId) {
      setPreview(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = unwrapApiBody(await playerAPI.getSelfLeavePreview(teamId));
      setPreview(res);
      setDone(false);
    } catch (e) {
      const body = e?.response?.data;
      if (body && typeof body === 'object') {
        setPreview(body);
      } else {
        setPreview({
          success: false,
          message: apiErrorMessage(e, t('phase2.playerHelpWizard.selfLeave.loadError')),
        });
      }
    } finally {
      setLoading(false);
    }
  }, [teamId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleLeave = async () => {
    if (!teamId || !password.trim()) {
      toast.error(t('phase2.playerHelpWizard.selfLeave.passwordRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const res = unwrapApiBody(await playerAPI.executeSelfLeave(teamId, { password }));
      if (res?.success !== false) {
        toast.success(res?.message || t('phase2.playerHelpWizard.selfLeave.success'));
        setDone(true);
        setPassword('');
        queryClient.invalidateQueries(['playerTeams']);
        wizard?.startOver?.();
      } else {
        toast.error(res?.message || t('phase2.playerHelpWizard.selfLeave.submitError'));
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, t('phase2.playerHelpWizard.selfLeave.submitError')));
    } finally {
      setSubmitting(false);
    }
  };

  if (!teamId) {
    return <Muted>{t('phase2.playerHelpWizard.pickTeamFirst')}</Muted>;
  }

  if (loading) {
    return <Muted>{t('phase2.playerHelpWizard.selfLeave.loading')}</Muted>;
  }

  if (done) {
    return (
      <Box>
        <Muted>{t('phase2.playerHelpWizard.selfLeave.doneHint')}</Muted>
      </Box>
    );
  }

  if (preview?.success === false) {
    return (
      <Box>
        <Muted>{preview.message || t('phase2.playerHelpWizard.selfLeave.unavailable')}</Muted>
      </Box>
    );
  }

  const teamLabel = preview?.team_name || teamContext?.teamName || '';

  return (
    <Box>
      <Caution>{t('phase2.playerHelpWizard.selfLeave.caution')}</Caution>
      <Summary>{t('phase2.playerHelpWizard.selfLeave.summaryMember', { team: teamLabel })}</Summary>
      <Field>
        {t('phase2.playerHelpWizard.selfLeave.passwordLabel')}
        <PasswordInput
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={submitting}
        />
      </Field>
      <LeaveBtn type="button" disabled={submitting || !password.trim()} onClick={handleLeave}>
        {submitting
          ? t('phase2.playerHelpWizard.selfLeave.submitting')
          : t('phase2.playerHelpWizard.selfLeave.confirmButton')}
      </LeaveBtn>
    </Box>
  );
}
